import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { FeldStatusZeichen, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { fetchKiEinstellungen, speichereKiEinstellungen, type KiEinstellungen as Einstellungen, type KiEinstellungenAenderung } from '../api/calculation-assistant.api'
import { fetchListenZuordnungen, loescheListenZuordnung, type ListenZuordnung } from '../api/calculation-ai.api'
import { FelderKarte, knopfPrimaer, type FeldDef } from './EinstellungsBausteine'

const GRENZEN: readonly FeldDef[] = [
  { feld: 'planPageLimit', label: 'Planblätter je Lauf höchstens', art: 'zahl', hinweis: 'Vorgabe 20' },
  { feld: 'folderDeleteDays', label: 'Ordner der KI-Aufträge löschen nach Tagen', art: 'zahl', hinweis: 'Vorgabe 7' },
]
/** N1: die eingebauten Fähigkeiten mit ihrem Namen (16.); weitere kommen mit N6, N7, N2. */
const FAEHIGKEIT: Record<string, { name: string; text: string }> = {
  V1: { name: 'Preise aus Angeboten', text: 'Lieferantenangebote lesen — frei im Reiter Dokumente oder zu einer Materialanfrage' },
  V2: { name: 'Stücklisten (PDF)', text: 'Material aus Stücklisten im PDF lesen — Excel und CSV liest Symphenia ohne KI' },
  V6: { name: 'Bieterlücken', text: 'offene Lücken des LV aus Artikeln, Befunden und früheren Antworten vorschlagen' },
  CHECK: { name: 'Prüfung vor der Abgabe', text: 'vergessene Kosten, Vorbemerkungen, Widersprüche und Mengen gegen die Unterlagen' },
}
const zahl = (n: number) => n.toLocaleString('de-DE')
const SCHLUESSEL = { TENANT: 'eigener Schlüssel des Betriebs', CENTRAL: 'Kontingent von Symphenia' } as const
const datum = (d: string | null) => (d ? new Date(d).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '')

/**
 * AP-37 Detailblatt N 16. — **Einstellungen → Kalkulation → KI** (Baustein `.AI`):
 * Assistent an/aus mit einmaliger Bestätigung des Datenwegs (8.5, KI-36), Schlüsselquelle
 * (Vorgabe: eigener Schlüssel, ohne stillen Rückfall), der eigene Schlüssel selbst (KI-76),
 * Einwilligung für die eingebauten Fähigkeiten mit Fassung (11.1) und die Grenzen.
 * Einschalten, Umschalten, Einwilligen und Schlüssel sind Handlungen mit Knopf; die Grenzen
 * speichern beim Verlassen des Felds.
 */
export function KiEinstellungen({ darfAendern }: { darfAendern: boolean }) {
  const { t } = useTranslation()
  const hasModule = useAuthStore((s) => s.hasModule)
  const [e, setE] = useState<Einstellungen | null>(null)
  const [schluessel, setSchluessel] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    void fetchKiEinstellungen()
      .then(setE)
      .catch((x) => setFehler(apiErrorText(x, 'Die KI-Einstellungen ließen sich nicht laden.')))
  }, [])

  // Schalter und Modell je Fähigkeit mit ihren Vorgaben (an, Modell des Mandanten) — die Felder sehen nie „leer".
  const daten = useMemo(
    () =>
      e
        ? ({
            ...e,
            capabilities: Object.fromEntries(e.builtinCapabilities.map((f) => [f, e.capabilities?.[f] !== false])),
            models: Object.fromEntries(e.builtinCapabilities.map((f) => [f, e.models?.[f] ?? ''])),
          } as unknown as Datensatz)
        : null,
    [e],
  )
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'calculationAiSettings',
    bezeichnung: 'KI-Einstellungen der Kalkulation',
    daten,
    schreibe: (patch) =>
      speichereKiEinstellungen(patch as KiEinstellungenAenderung).then((n) => {
        setE(n)
        return { data: n } as unknown as Datensatz
      }),
  }) as unknown as Speicherstelle<unknown>

  async function tu(dto: KiEinstellungenAenderung) {
    setLaeuft(true)
    setFehler(null)
    try {
      setE(await speichereKiEinstellungen(dto))
      return true
    } catch (x) {
      setFehler(apiErrorText(x, 'Das ließ sich nicht speichern.'))
      return false
    } finally {
      setLaeuft(false)
    }
  }

  if (!e) return <p className="text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</p>

  const anbieter = e.provider === 'ANTHROPIC' ? 'Claude (Anthropic)' : e.provider

  // Der Text der Bestätigung kommt ganz vom Server (Fassung `kalkulation-assistent/1`) — kein Zusatz der Oberfläche:
  // Dienst und Ort der Verarbeitung stehen im KI-Zusatz, nicht hier (10.10.2026, Befund R-16).
  async function einschalten() {
    if (!(await bestaetige(e!.assistantConfirmationText, { ok: 'Bestätigen und einschalten' }))) return
    await tu({ assistantEnabled: true, confirmAssistant: true })
  }
  async function umschalten(quelle: 'TENANT' | 'CENTRAL') {
    if (quelle === e!.assistantKeySource) return
    // Ein anderer Datenweg will neu bestätigt sein (8.5) — bei ausgeschaltetem Assistenten erst beim Einschalten.
    if (e!.assistantEnabled) {
      if (!(await bestaetige(e!.assistantConfirmationText, { ok: 'Bestätigen und umstellen' }))) return
      await tu({ assistantKeySource: quelle, confirmAssistant: true })
    } else await tu({ assistantKeySource: quelle })
  }

  const ohneSchluessel = e.assistantKeySource === 'TENANT' && !e.tenantKey
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Assistent</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Seitenleiste in der Kalkulation: beantwortet Fragen mit Zahlen aus der Kalkulation und legt Änderungen als Vorschläge an — er schreibt nie selbst.
        </p>
        {/* AP-18 7.5 (AS-41): Mit dem allgemeinen Assistenten gelten dessen Einwilligung und Schlüssel — auch in der Kalkulation. */}
        {hasModule('AI_ASSISTANT') && (
          <p className="mt-3 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            {t('assistant.calculation.settingsMoved')}{' '}
            <Link to="/settings/ai" className="text-primary hover:underline">
              {t('assistant.calculation.settingsLink')}
            </Link>
          </p>
        )}
        <div className="mt-3 space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-3">
            <span className="w-40 text-muted-foreground">Zustand</span>
            <span className="font-medium">{e.assistantEnabled && e.assistantConfirmedAt ? 'eingeschaltet' : 'ausgeschaltet'}</span>
            {darfAendern &&
              (e.assistantEnabled && e.assistantConfirmedAt ? (
                <button type="button" className="rounded-md border border-border px-3 py-1 text-sm" disabled={laeuft} onClick={() => void tu({ assistantEnabled: false })}>
                  Ausschalten
                </button>
              ) : (
                <button type="button" className={knopfPrimaer} disabled={laeuft} onClick={() => void einschalten()}>
                  Einschalten …
                </button>
              ))}
          </div>
          {e.assistantConfirmedAt && <p className="text-xs text-muted-foreground">Datenweg bestätigt am {datum(e.assistantConfirmedAt)}.</p>}
          <div className="flex flex-wrap items-center gap-3">
            <span className="w-40 text-muted-foreground">Tokens bezahlt über</span>
            <select className={`${inlineInput} !w-auto`} value={e.assistantKeySource} disabled={!darfAendern || laeuft} onChange={(x) => void umschalten(x.target.value as 'TENANT' | 'CENTRAL')}>
              <option value="TENANT">{SCHLUESSEL.TENANT}</option>
              <option value="CENTRAL">{SCHLUESSEL.CENTRAL}</option>
            </select>
          </div>
          {ohneSchluessel && <p className="rounded-md bg-amber-500/15 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">Kein eigener Schlüssel hinterlegt — der Assistent antwortet erst mit Schlüssel. Er weicht nicht still auf das Kontingent von Symphenia aus.</p>}
        </div>
      </div>

      <div className={formCardSm}>
        <h3 className={cardTitle}>Eigener KI-Schlüssel</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          API-Schlüssel des Betriebs bei {anbieter}. Er wird verschlüsselt abgelegt und nie wieder angezeigt; derselbe Schlüssel gilt unter Einstellungen → KI.
        </p>
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex items-center gap-3">
            <span className="w-40 text-muted-foreground">Hinterlegt</span>
            <span className="font-medium">{e.tenantKey ? `ja, endet auf …${e.tenantKeySuffix ?? ''}` : 'nein'}</span>
            {e.tenantKey && darfAendern && (
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1 text-sm"
                disabled={laeuft}
                onClick={async () => {
                  if (await bestaetige('Eigenen Schlüssel entfernen? Mit „eigener Schlüssel" antwortet der Assistent danach nicht mehr.', { ok: 'Entfernen', gefaehrlich: true })) await tu({ removeApiKey: true })
                }}
              >
                Entfernen
              </button>
            )}
          </div>
          {darfAendern && (
            <div className="flex flex-wrap items-center gap-2">
              <input type="password" autoComplete="off" className={`${inlineInput} !w-72`} placeholder="sk-ant-…" value={schluessel} onChange={(x) => setSchluessel(x.target.value)} />
              <button
                type="button"
                className={knopfPrimaer}
                disabled={laeuft || schluessel.trim().length < 20}
                onClick={async () => {
                  if (await tu({ apiKey: schluessel.trim() })) setSchluessel('')
                }}
              >
                {e.tenantKey ? 'Ersetzen' : 'Hinterlegen'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={formCardSm}>
        <h3 className={cardTitle}>Einwilligung</h3>
        <p className="mt-1 text-xs text-muted-foreground">Gilt für die eingebauten Fähigkeiten (Unterlagen lesen, Preise, Vorkalkulation, Prüfung). Fassung {e.consentTextVersion}.</p>
        <p className="mt-2 text-sm">{e.consentText}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium">{e.consentCurrent ? `erteilt am ${datum(e.consentAt)}` : e.consentVersion ? 'für eine ältere Fassung erteilt — bitte neu einwilligen' : 'nicht erteilt'}</span>
          {darfAendern &&
            (e.consentCurrent ? (
              <button type="button" className="rounded-md border border-border px-3 py-1 text-sm" disabled={laeuft} onClick={() => void tu({ consent: false })}>
                Zurückziehen
              </button>
            ) : (
              <button type="button" className={knopfPrimaer} disabled={laeuft} onClick={() => void tu({ consent: true })}>
                Einwilligen
              </button>
            ))}
        </div>
      </div>

      <div className={formCardSm}>
        <h3 className={cardTitle}>Eingebaute Fähigkeiten</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Was Symphenia selbst mit der KI erledigt. Braucht die Einwilligung; ohne eigenen Schlüssel zählt es auf das Kontingent.
        </p>
        <div className="mt-3 space-y-3 text-sm">
          {e.builtinCapabilities.map((f) => (
            <FaehigkeitZeile key={f} stelle={stelle} faehigkeit={f} modelle={e.availableModels} vorgabe={e.defaultModels?.[f] ?? e.tenantModel ?? e.availableModels?.defaultModel ?? ''} darf={darfAendern} />
          ))}
          <div className="border-t border-border pt-2 text-xs text-muted-foreground">
            Kontingent dieses Monats (Schlüssel von Symphenia):{' '}
            <span className={e.quota.warn ? 'font-medium text-amber-700 dark:text-amber-300' : 'font-medium text-foreground'}>
              {zahl(e.quota.used)} von {zahl(e.quota.limit)} Tokens
            </span>
            {e.tenantKey && ' — mit eigenem Schlüssel ohne Grenze.'}
          </div>
        </div>
      </div>

      <FelderKarte titel="Grenzen" felder={GRENZEN} stelle={stelle} darfAendern={darfAendern} />
      <Zuordnungen darfAendern={darfAendern} />
      {fehler && <p className="text-sm text-destructive lg:col-span-2">{fehler}</p>}
    </div>
  )
}

function FaehigkeitZeile({
  stelle,
  faehigkeit,
  modelle,
  vorgabe,
  darf,
}: {
  stelle: Speicherstelle<unknown>
  faehigkeit: string
  modelle: { models: string[]; defaultModel: string } | null
  vorgabe: string
  darf: boolean
}) {
  const an = useFeld<boolean>(stelle, `capabilities.${faehigkeit}`, { art: 'schalter', label: FAEHIGKEIT[faehigkeit]?.name ?? faehigkeit, gesperrt: !darf })
  const modell = useFeld<string | null>(stelle, `models.${faehigkeit}`, { art: 'auswahl', label: 'Modell', gesperrt: !darf, lies: (t: string) => t, schreib: (w: string | null) => w ?? '' })
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex min-w-[14rem] flex-1 items-start gap-2">
        <input type="checkbox" className="mt-0.5" {...an.schalter} />
        <span>
          <span className="font-medium">{FAEHIGKEIT[faehigkeit]?.name ?? faehigkeit}</span>
          <span className="block text-xs text-muted-foreground">{FAEHIGKEIT[faehigkeit]?.text}</span>
        </span>
        <FeldStatusZeichen status={an} />
      </label>
      <span className="flex items-center gap-1">
        <select className={`${feldKlasse} !w-auto`} aria-label="Modell" {...modell.props}>
          <option value="">Vorgabe ({vorgabe})</option>
          {(modelle?.models ?? []).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <FeldStatusZeichen status={modell} />
      </span>
    </div>
  )
}

const FELD_NAME: Record<string, string> = { bezeichnung: 'Bezeichnung', menge: 'Menge', einheit: 'Einheit', laenge: 'Länge', gewicht: 'Gewicht', werkstoff: 'Werkstoff', position: 'Position' }

/** N2 (16.): gemerkte Spaltenzuordnungen für Stücklisten aus Excel/CSV — je Kopfzeile eine; löschen heißt beim nächsten Mal neu erkennen. */
function Zuordnungen({ darfAendern }: { darfAendern: boolean }) {
  const [liste, setListe] = useState<ListenZuordnung[] | null>(null)
  const laden = () =>
    void fetchListenZuordnungen()
      .then(setListe)
      .catch(() => setListe([]))
  useEffect(laden, [])
  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>Gemerkte Spaltenzuordnungen</h3>
      <p className="mt-1 text-xs text-muted-foreground">Stücklisten aus Excel oder CSV: je Kopfzeile, welche Spalte welches Feld ist. Gelesen wird ohne KI.</p>
      {liste?.length === 0 && <p className="mt-3 text-sm text-muted-foreground">Noch keine.</p>}
      <ul className="mt-3 space-y-2 text-sm">
        {liste?.map((z) => (
          <li key={z.id} className="flex items-start justify-between gap-2 rounded-md border border-border/60 px-2.5 py-1.5">
            <span className="min-w-0">
              <span className="block truncate text-xs text-muted-foreground" title={z.signature}>
                {z.signature.split('|').join(' · ')}
              </span>
              {Object.entries(z.columns)
                .filter(([f, h]) => FELD_NAME[f] && h)
                .map(([f, h]) => `${FELD_NAME[f]} ← ${String(h)}`)
                .join(', ')}
            </span>
            {darfAendern && (
              <button type="button" className="shrink-0 text-xs text-muted-foreground hover:text-destructive" onClick={() => void loescheListenZuordnung(z.id).then(laden)}>
                löschen
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
